import { useState, useEffect, useCallback } from "react";
import { updateService, createService, deleteService as apiDeleteService, CMSServiceCreate, CMSServiceUpdate } from "@/api/services";
import { useDataContext, ServiceDetail } from "@/context/DataContext";

export type { ServiceDetail };

// ─── Public hook (for public-facing pages) ────────────────────────────────────
export function usePublicServices() {
  const { services, reloadServices } = useDataContext();
  useEffect(() => {
    reloadServices();
  }, [reloadServices]);
  const activeServices = services.filter((s) => (s as any).status !== "inactive");
  return { services: activeServices };
}

// ─── CMS Admin hook (for CMS admin pages) ────────────────────────────────────
export function useCMSServices() {
  const {
    services: globalServices,
    servicesLoading: loading,
    servicesError: error,
    reloadServices: reload,
  } = useDataContext();

  const [services, setServices] = useState<ServiceDetail[]>(globalServices);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hasChanges, setHasChanges] = useState(false);

  // Keep local editor state in sync with global state changes
  useEffect(() => {
    setServices(globalServices);
  }, [globalServices]);

  const saveOne = useCallback(async (s: ServiceDetail) => {
    setSaveStatus("saving");
    try {
      const apiData = {
        service_id: s.service_id || s.id,
        name: s.name,
        name_en: s.nameEn,
        tagline: s.tagline,
        tagline_en: s.taglineEn,
        description: s.description,
        description_en: s.descriptionEn,
        long_description: s.longDescription,
        long_description_en: s.longDescriptionEn,
        icon: s.icon,
        accent_color: s.accentColor,
        image: s.image,
        hero_image: s.heroImage,
        category: s.category,
        stats: s.stats,
        procedures: s.procedures,
        prices: s.prices,
        doctors: s.doctors,
        faqs: s.faqs,
        related_services: s.relatedServices,
        status: s.status,
        show_price: s.showPrice !== false,
      };
      
      // Update existing using its internal 'id' which matches backend UUID
      if (s.id && !s.id.startsWith("srv-") && s.id.length > 20) {
        await updateService(s.id, apiData as CMSServiceUpdate);
      } else {
        await createService(apiData as CMSServiceCreate);
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    setSaveStatus("saving");
    try {
      await apiDeleteService(id);
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const save = useCallback(async (data: ServiceDetail[]) => {
    setSaveStatus("saving");
    try {
      for (const s of data) {
        const apiData = {
          service_id: s.service_id || s.id,
          name: s.name,
          name_en: s.nameEn,
          tagline: s.tagline,
          tagline_en: s.taglineEn,
          description: s.description,
          description_en: s.descriptionEn,
          long_description: s.longDescription,
          long_description_en: s.longDescriptionEn,
          icon: s.icon,
          accent_color: s.accentColor,
          image: s.image,
          hero_image: s.heroImage,
          category: s.category,
          stats: s.stats,
          procedures: s.procedures,
          prices: s.prices,
          doctors: s.doctors,
          faqs: s.faqs,
          related_services: s.relatedServices,
          status: s.status,
          show_price: s.showPrice !== false,
        };
        
        if (s.id && !s.id.startsWith("srv-") && s.id.length > 20) {
          await updateService(s.id, apiData as CMSServiceUpdate);
        } else {
          await createService(apiData as CMSServiceCreate);
        }
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const updateServices = useCallback((data: ServiceDetail[]) => {
    setServices(data);
    setHasChanges(true);
  }, []);

  const reset = useCallback(async () => {
    await reload();
    setHasChanges(false);
  }, [reload]);

  return {
    services,
    loading,
    error,
    saveStatus,
    hasChanges,
    updateServices,
    save,
    saveOne,
    remove,
    reset,
    reload,
  };
}
